import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BarChart3, BookOpen, Users, AlertTriangle, RefreshCw, Barcode, Printer, BookCopy, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import BarcodeComponent from 'react-barcode';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' };
};

const BooksReports = () => {
  const [summary, setSummary] = useState(null);
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [damagedBooks, setDamagedBooks] = useState([]);
  const [allBooks, setAllBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBarcodeReport, setShowBarcodeReport] = useState(false);
  const barcodeReportRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, issuedRes, damagedRes, booksRes] = await Promise.all([
        axios.get(`${API_URL}/api/library/reports/summary`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/library/issues?status=Issued`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/library/damaged-books`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/library/books?limit=10000`, { headers: getAuthHeaders() })
      ]);
      setSummary(summaryRes.data || {});
      setIssuedBooks(Array.isArray(issuedRes.data) ? issuedRes.data : []);
      setDamagedBooks(Array.isArray(damagedRes.data) ? damagedRes.data : []);
      const booksData = Array.isArray(booksRes.data) ? booksRes.data : (booksRes.data?.data || []);
      setAllBooks(booksData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  const handlePrintAllBarcodes = () => {
    const printContent = barcodeReportRef.current;
    if (!printContent) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><title>All Book Barcodes</title>
      <style>body{font-family:Arial;padding:10px}.barcode-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
      .barcode-item{border:1px dashed #ccc;padding:10px;text-align:center;page-break-inside:avoid}
      .book-title{font-size:10px;font-weight:bold;margin-bottom:3px}.book-info{font-size:8px;color:#666;margin-bottom:5px}
      @media print{.barcode-item{border:1px solid #eee}@page{margin:0.5cm}}</style></head>
      <body><h2 style="text-align:center;margin-bottom:20px">Library Book Barcodes</h2>${printContent.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getOverdueBooks = () => {
    const today = new Date();
    return issuedBooks.filter(issue => new Date(issue.due_date) < today);
  };

  const getPopularBooks = () => {
    const bookCounts = {};
    issuedBooks.forEach(issue => {
      bookCounts[issue.book_title] = (bookCounts[issue.book_title] || 0) + 1;
    });
    return Object.entries(bookCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([title, count]) => ({ title, count }));
  };

  if (loading) {
    return <div className="p-6 flex justify-center"><p>Loading reports...</p></div>;
  }

  const overdueBooks = getOverdueBooks();
  const popularBooks = getPopularBooks();
  const s = summary || {};
  const utilizationPct = s.total_copies > 0 ? Math.round((s.issued_copies / s.total_copies) * 100) : 0;

  return (
    <div className="p-6 space-y-6" data-testid="books-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6" /> Library Reports & Analytics
        </h1>
        <Button onClick={fetchData} variant="outline" size="sm"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      {/* Summary Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-center">
              <BookOpen className="w-7 h-7 text-blue-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Total Books</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="total-books">{s.total_books || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-center">
              <BookCopy className="w-7 h-7 text-green-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Total Copies</p>
              <p className="text-2xl font-bold text-green-600" data-testid="total-copies">{s.total_copies || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-center">
              <CheckCircle className="w-7 h-7 text-emerald-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Available</p>
              <p className="text-2xl font-bold text-emerald-600" data-testid="available-copies">{s.available_copies || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-center">
              <Clock className="w-7 h-7 text-yellow-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Issued</p>
              <p className="text-2xl font-bold text-yellow-600" data-testid="issued-copies">{s.issued_copies || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-center">
              <AlertTriangle className="w-7 h-7 text-red-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Overdue</p>
              <p className="text-2xl font-bold text-red-600" data-testid="overdue-books">{s.overdue_books || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="text-center">
              <Users className="w-7 h-7 text-purple-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Members</p>
              <p className="text-2xl font-bold text-purple-600" data-testid="total-members">{(s.total_members || 0) - (s.blocked_members || 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Copy Status Breakdown + Utilization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Copy Status */}
        <Card>
          <CardHeader><CardTitle className="text-base">Copy Status Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { label: 'Available', value: s.available_copies || 0, color: 'bg-green-500', textColor: 'text-green-600' },
                { label: 'Issued', value: s.issued_copies || 0, color: 'bg-yellow-500', textColor: 'text-yellow-600' },
                { label: 'Damaged', value: s.damaged_copies || 0, color: 'bg-orange-500', textColor: 'text-orange-600' },
                { label: 'Lost', value: s.lost_copies || 0, color: 'bg-red-500', textColor: 'text-red-600' },
              ].map(item => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{item.label}</span>
                    <span className={`font-bold ${item.textColor}`}>{item.value}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`${item.color} h-2 rounded-full`}
                      style={{ width: s.total_copies > 0 ? `${Math.max(1, (item.value / s.total_copies) * 100)}%` : '0%' }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Category-wise Books */}
        <Card>
          <CardHeader><CardTitle className="text-base">Books by Category</CardTitle></CardHeader>
          <CardContent>
            {(s.category_stats || []).length === 0 ? (
              <p className="text-gray-500 text-sm">No category data available</p>
            ) : (
              <div className="space-y-2">
                {(s.category_stats || []).map((cat, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{cat.category}</span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-blue-600">{cat.books} books</span>
                      <span className="text-xs text-gray-500 ml-2">({cat.copies} copies)</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Library Activity */}
        <Card>
          <CardHeader><CardTitle className="text-base">Library Activity (Last 30 Days)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{s.recent_issues || 0}</p>
                <p className="text-sm text-blue-700">Books Issued</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{s.recent_returns || 0}</p>
                <p className="text-sm text-green-700">Books Returned</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-700">Collection Utilization</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div className="bg-purple-500 h-3 rounded-full" style={{ width: `${utilizationPct}%` }} />
                  </div>
                  <span className="text-sm font-bold text-purple-600">{utilizationPct}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Popular Books + Circulation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Books */}
        <Card>
          <CardHeader><CardTitle>Most Popular Books</CardTitle></CardHeader>
          <CardContent>
            {popularBooks.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">No circulation data available</p>
            ) : (
              <div className="space-y-3">
                {popularBooks.map((book, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>#{index + 1}</span>
                      <span className="font-medium text-sm">{book.title}</span>
                    </div>
                    <span className="font-bold text-blue-600">{book.count} issues</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Currently Issued */}
        <Card>
          <CardHeader>
            <CardTitle>Currently Issued ({issuedBooks.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {issuedBooks.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">No books currently issued</p>
            ) : (
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b">
                      <th className="text-left p-2">Book</th>
                      <th className="text-left p-2">Accession No</th>
                      <th className="text-left p-2">Member</th>
                      <th className="text-left p-2">Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issuedBooks.slice(0, 20).map(issue => {
                      const overdue = new Date(issue.due_date) < new Date();
                      return (
                        <tr key={issue.id} className={`border-b ${overdue ? 'bg-red-50' : ''}`}>
                          <td className="p-2 font-medium">{issue.book_title}</td>
                          <td className="p-2">
                            {issue.accession_number ? (
                              <span className="font-mono text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{issue.accession_number}</span>
                            ) : '-'}
                          </td>
                          <td className="p-2">{issue.member_name}</td>
                          <td className={`p-2 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                            {formatDate(issue.due_date)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Overdue Books */}
      {overdueBooks.length > 0 && (
        <Card className="border-red-200">
          <CardHeader><CardTitle className="text-red-600">Overdue Books ({overdueBooks.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-red-50">
                    <th className="text-left p-3 text-sm">Book Title</th>
                    <th className="text-left p-3 text-sm">Accession No</th>
                    <th className="text-left p-3 text-sm">Member</th>
                    <th className="text-left p-3 text-sm">Due Date</th>
                    <th className="text-left p-3 text-sm">Days Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueBooks.slice(0, 15).map(issue => {
                    const daysOverdue = Math.ceil((new Date() - new Date(issue.due_date)) / 86400000);
                    return (
                      <tr key={issue.id} className="border-b hover:bg-red-50">
                        <td className="p-3 font-medium">{issue.book_title}</td>
                        <td className="p-3">
                          {issue.accession_number ? (
                            <span className="font-mono text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{issue.accession_number}</span>
                          ) : '-'}
                        </td>
                        <td className="p-3">{issue.member_name}<div className="text-xs text-gray-500">{issue.member_card_number}</div></td>
                        <td className="p-3 text-red-600">{formatDate(issue.due_date)}</td>
                        <td className="p-3"><span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">{daysOverdue} days</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Damaged Books */}
      {damagedBooks.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader><CardTitle className="text-orange-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Damaged Books ({damagedBooks.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-orange-50">
                    <th className="text-left p-3 text-sm">Book Title</th>
                    <th className="text-left p-3 text-sm">Member</th>
                    <th className="text-left p-3 text-sm">Damage Type</th>
                    <th className="text-left p-3 text-sm">Date</th>
                    <th className="text-left p-3 text-sm">Fine</th>
                    <th className="text-left p-3 text-sm">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {damagedBooks.map(damage => (
                    <tr key={damage.id} className="border-b hover:bg-orange-50">
                      <td className="p-3 font-medium">{damage.book_title}</td>
                      <td className="p-3">{damage.member_name || 'N/A'}</td>
                      <td className="p-3"><span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">{damage.damage_type}</span></td>
                      <td className="p-3 text-sm">{damage.damage_date ? formatDate(damage.damage_date) : 'N/A'}</td>
                      <td className="p-3 font-bold text-orange-600">{damage.penalty_amount}</td>
                      <td className="p-3">{damage.penalty_paid ?
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Paid</span> :
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Pending</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-orange-50 font-bold">
                    <td colSpan="4" className="p-3 text-right">Total Fines:</td>
                    <td className="p-3 text-orange-600">{damagedBooks.reduce((s, d) => s + (d.penalty_amount || 0), 0)}</td>
                    <td className="p-3 text-green-600 text-sm">Paid: {damagedBooks.filter(d => d.penalty_paid).reduce((s, d) => s + (d.penalty_amount || 0), 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Barcode Report */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2"><Barcode className="w-5 h-5" /> Book Barcodes Report</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowBarcodeReport(!showBarcodeReport)}>
              {showBarcodeReport ? 'Hide Barcodes' : 'Show Barcodes'}
            </Button>
            {showBarcodeReport && <Button size="sm" onClick={handlePrintAllBarcodes}><Printer className="w-4 h-4 mr-2" /> Print All</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {!showBarcodeReport ? (
            <div className="text-center py-8 text-gray-500">
              <Barcode className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Click "Show Barcodes" to view all book barcodes</p>
              <p className="text-sm">Total books: {allBooks.length}</p>
            </div>
          ) : (
            <div ref={barcodeReportRef}>
              <div className="barcode-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allBooks.map(book => (
                  <div key={book.id} className="barcode-item border rounded-lg p-3 text-center bg-white">
                    <p className="book-title font-semibold text-xs mb-1 truncate">{book.title}</p>
                    <p className="book-info text-xs text-gray-500 mb-2">{book.author_name} | Rack: {book.rack_number}</p>
                    {book.barcode ? <BarcodeComponent value={book.barcode} width={1.2} height={40} fontSize={10} margin={3} /> : <p className="text-xs text-red-500">No barcode</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BooksReports;
